import React, { FC, useCallback, useContext } from 'react';
import LeadsContext from '../../../../providers/Leads/Leads.context';
import api from 'api';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { Note, NoteFile, NoteTag } from 'models/Note';
import useQueryParams from 'hooks/useQueryParams';
import { NotesList } from '../../../NotesList';
import { useTranslation } from 'react-i18next';
import { AxiosRequestConfig } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import { getHasElementsInArray, getUserFullName } from 'utils';
import { ReactionType } from 'models/Reactions';
import { updateNoteReaction } from '../../../NotesList/utils';

type LeadNotesProps = {
  onNotesChange: (hasNotes: boolean) => void;
};

const LeadNotes: FC<LeadNotesProps> = ({ onNotesChange }) => {
  const { selectedLead, updateLead } = useContext(LeadsContext);

  const {
    params: { $sort },
  } = useQueryParams();
  const { t } = useTranslation();

  const {
    loading,
    items: notes,
    setItems: setNotes,
    onContainerScrolled: onNotesScroll,
  } = useInfinitePagination<Note>({
    condition: !!selectedLead?._id,
    resetDeps: [selectedLead?._id, $sort],
    dependencies: [selectedLead?._id, $sort],
    onItemsLoad: (_, items) => onNotesChange(getHasElementsInArray(items)),
    makeRequest: async (currentPage: number) => {
      return api.portfolio.seller
        .getLeadNotes(
          {
            $page: currentPage.toString(),
            $limit: '200',
            $sort: $sort || '-createdAt',
          },
          selectedLead?._id,
        )
        .then((res) => res.data);
    },
  });

  const handleOnSendNote = useCallback(
    async (
      content: string,
      extractedTags: NoteTag[],
      uploadedFiles: DropzoneFile[],
    ) => {
      const { data: sentNote } = await api.portfolio.seller.sendLeadNote(
        selectedLead?._id,
        content,
        extractedTags,
        uploadedFiles,
      );

      const shouldBeFirst = !$sort || $sort === '-createdAt';

      onNotesChange(true);
      setNotes((prev) =>
        shouldBeFirst ? [sentNote, ...prev] : [...prev, sentNote],
      );

      updateLead({
        ...selectedLead,
        unreadNotesCount: 0,
        unrepliedNotesCount: 0,
      });
    },
    [$sort, selectedLead, setNotes, updateLead, onNotesChange],
  );

  const removeNote = useCallback(
    async (noteId: string) => {
      await api.portfolio.seller.archiveLeadNote(noteId);

      setNotes((oldNotes) => {
        const newNotes = oldNotes.filter(({ _id }) => _id !== noteId);

        updateLead({
          ...selectedLead,
          unreadNotesCount: 0,
          unrepliedNotesCount:
            selectedLead?.unrepliedNotesCount > newNotes.length
              ? newNotes.length
              : selectedLead?.unrepliedNotesCount || 0,
        });

        return newNotes;
      });
    },
    [selectedLead, setNotes, updateLead],
  );

  const removeNoteFile = useCallback(
    (fileId: string) =>
      setNotes((old) =>
        old.map((note) => ({
          ...note,
          files: note.files.filter(({ _id }) => _id !== fileId),
        })),
      ),
    [setNotes],
  );

  const updateNoteFile = useCallback(
    (updated: Partial<NoteFile>) =>
      setNotes((old) =>
        old.map((note) => ({
          ...note,
          files: note.files.map((file) =>
            file._id === updated._id ? { ...file, ...updated } : file,
          ),
        })),
      ),
    [setNotes],
  );

  const onUploadReq = useCallback(
    (file: File, config: AxiosRequestConfig) =>
      api.portfolio.seller.uploadLeadNoteFile(selectedLead?._id, file, config),
    [selectedLead?._id],
  );

  const onFilePreview = useCallback(
    (fileId: string, noteId: string) =>
      api.portfolio.seller.previewNoteFile(noteId, fileId),
    [],
  );

  const onFileRemove = useCallback(
    async (fileId: string, removeFromDataRoom: boolean, noteId: string) => {
      await api.portfolio.seller.removeNoteFile(
        noteId,
        fileId,
        removeFromDataRoom,
      );
      removeNoteFile(fileId);
    },
    [removeNoteFile],
  );

  const onFileAddToDataRoom = useCallback(
    async (fileId: string, noteId: string) => {
      await api.portfolio.seller.moveFileToDataRoom(noteId, fileId);
      updateNoteFile({ _id: fileId, isDataRoomFile: true });
    },
    [updateNoteFile],
  );

  const onReact = useCallback(
    (noteId: string, reaction: ReactionType) => {
      api.portfolio.seller.addNoteReaction(noteId, reaction);
      setNotes((old) => updateNoteReaction(old, noteId, reaction));
    },
    [setNotes],
  );

  if (!selectedLead) return null;

  return (
    <NotesList
      focusTrigger={selectedLead?._id}
      loading={loading}
      notes={notes}
      notesAbout={getUserFullName(
        selectedLead?.firstName,
        selectedLead?.lastName,
      )}
      emptyMessage={t('createFirstBuyerNote')}
      handleOnSendNote={handleOnSendNote}
      removeNote={removeNote}
      onNotesScroll={onNotesScroll}
      onUploadReq={onUploadReq}
      onFilePreview={onFilePreview}
      onFileRemove={onFileRemove}
      onFileAddToDataRoom={onFileAddToDataRoom}
      onReact={onReact}
      getReactionUsers={api.portfolio.seller.getNoteReactions}
    />
  );
};

export default LeadNotes;
